import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

import SearchBar from '../../../components/common/search/SearchBar';
import FilterSelect from '../../../components/common/filter/FilterSelect';
import DateFilter from '../../../components/common/filter/DateFilter';
import usePendingTeachers from '../../../hooks/usePendingTeachers';
import ConfirmDialog from '../../../components/common/popups/ConfirmDialog';
import PrimaryButton from '../../../components/common/buttons/PrimaryButton';
import {fetchEduLevelSubjects} from '../../../services/masterData.service';

export default function PendingApprovals() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [appliedSubjectFilter, setAppliedSubjectFilter] = useState('');
  const [appliedDateFilter, setAppliedDateFilter] = useState('');
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    teacherId: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const { teachers, meta, loading, actionLoading, error, approve, reject, refetch } =
    usePendingTeachers(page + 1, rowsPerPage, {
      search: appliedSearchQuery,
      subject: appliedSubjectFilter,
      date: appliedDateFilter,
    }); // MUI page is 0-based, API is 1-based

  useEffect(() => {
    let isMounted = true;

    const loadSubjects = async () => {
      try {
        const res = await fetchEduLevelSubjects();
        const options = (res.data ?? []).map((subject) => ({
          value: subject.id,
          label: subject.name,
        }));

        if (isMounted) setSubjectOptions(options);
      } catch (err) {
        if (isMounted) setSubjectOptions([]);
      }
    };

    loadSubjects();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasActiveFilters = appliedSearchQuery || appliedSubjectFilter || appliedDateFilter;
  const hasFilterChanges =
    searchQuery.trim() !== appliedSearchQuery ||
    subjectFilter !== appliedSubjectFilter ||
    dateFilter !== appliedDateFilter;

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleSubjectFilterChange = (value) => {
    setSubjectFilter(value);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
  };

  const applyFilters = () => {
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedSubjectFilter(subjectFilter);
    setAppliedDateFilter(dateFilter);
    setPage(0);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSubjectFilter('');
    setDateFilter('');
    setAppliedSearchQuery('');
    setAppliedSubjectFilter('');
    setAppliedDateFilter('');
    setPage(0);
  };

  const handleApprove = async (id) => {
    const result = await approve(id);
    setSnackbar({
      open: true,
      message: result.message,
      severity: result.success ? 'success' : 'error',
    });
  };

  const handleRejectClick = (id) => {
    setConfirmDialog({ open: true, teacherId: id });
  };

  const handleConfirmReject = async () => {
    const result = await reject(confirmDialog.teacherId);
    setConfirmDialog({ open: false, teacherId: null });
    setSnackbar({
      open: true,
      message: result.message,
      severity: result.success ? 'success' : 'error',
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <CircularProgress sx={{ color: '#F6911D' }} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-red-400 text-lg">{error}</p>
        <PrimaryButton onClick={refetch}>Try Again</PrimaryButton>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-light mb-2">Pending Approvals</h1>
        <p className="text-gray-400">Review and approve teacher applications</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-glass backdrop-blur-sm p-4 rounded-lg border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Pending</p>
              <p className="text-2xl font-bold text-light">{meta?.total ?? 0}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-primary rounded-lg flex items-center justify-center">
              <EmailOutlinedIcon sx={{ color: 'white' }} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-glass backdrop-blur-sm p-4 rounded-lg border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">This Week</p>
              <p className="text-2xl font-bold text-light">
                {
                  teachers.filter((t) => {
                    const created = new Date(t.created_at);
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return created >= weekAgo;
                  }).length
                }
              </p>
            </div>
            <div className="w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center border border-secondary/30">
              <CalendarMonthOutlinedIcon sx={{ color: 'white' }} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-glass backdrop-blur-sm p-4 rounded-lg border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Current Page</p>
              <p className="text-2xl font-bold text-light">
                {meta ? `${meta.current_page} / ${meta.last_page}` : '—'}
              </p>
            </div>
            <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center border border-accent/30">
              <AccessTimeOutlinedIcon sx={{ color: 'white' }} />
            </div>
          </div>
        </div>
      </div>

      <div className="border border-light/20 p-6 rounded-lg">
        {/* Search and Filters */}
        <div className="bg-gradient-glass backdrop-blur-sm p-4 rounded-lg border border-white/10 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <SearchBar
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name or email..."
              />
            </div>
            <FilterSelect
              label="Subject"
              value={subjectFilter}
              onChange={handleSubjectFilterChange}
              options={subjectOptions}
            />
            <DateFilter label="Applied Date" value={dateFilter} onChange={handleDateFilterChange} />
            <Button
              variant="contained"
              disabled={!hasFilterChanges}
              onClick={applyFilters}
              sx={{
                height: '56px',
                background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { opacity: 0.9 },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.35)',
                },
              }}
            >
              Filter
            </Button>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-gray-400">
                Showing {teachers.length} of {meta?.total ?? teachers.length} results
              </p>
              <Button
                size="small"
                onClick={clearFilters}
                sx={{
                  color: '#F6911D',
                  textTransform: 'none',
                  '&:hover': { background: 'rgba(246, 145, 29, 0.1)' },
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <TableContainer
          className="!bg-transparent"
          component={Paper}
          sx={{ borderRadius: '12px', overflow: 'auto' }}
        >
          <Table className="w-full bg-gradient-glass backdrop-blur-sm rounded-lg shadow-lg border border-white/10 [&_.MuiTableCell-root]:border-gray-700/50">
            <TableHead className="bg-gray-800/50">
              <TableRow>
                <TableCell className="!text-white !font-semibold !text-base">Applicant</TableCell>
                <TableCell className="!text-white !font-semibold !text-base">Email</TableCell>
                <TableCell className="!text-white !font-semibold !text-base">Phone</TableCell>
                <TableCell className="!text-white !font-semibold !text-base">Subjects</TableCell>
                <TableCell className="!text-white !font-semibold !text-base">
                  Applied Date
                </TableCell>
                <TableCell className="!text-white !font-semibold !text-base" align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teachers.length > 0 ? (
                teachers.map((teacher) => {
                  const fullName = `${teacher.user.first_name} ${teacher.user.last_name}`;
                  const isActing = actionLoading === teacher.id;

                  return (
                    <TableRow
                      key={teacher.id}
                      className="hover:!bg-white/5 transition-colors duration-200"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            src={teacher.avatar ?? undefined}
                            sx={{
                              width: 40,
                              height: 40,
                              background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
                              fontWeight: 'bold',
                            }}
                          >
                            {teacher.user.first_name.charAt(0)}
                          </Avatar>
                          <span className="!text-gray-200 font-medium">{fullName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="!text-gray-300">
                        {teacher.user.email}
                      </TableCell>
                      <TableCell className="!text-gray-300">
                        {teacher.user.phone}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {teacher.subjects?.length > 0 ? (
                            teacher.subjects.map((s) => (
                              <Chip
                                key={s.id}
                                label={s.name}
                                size="small"
                                sx={{
                                  background:
                                    'linear-gradient(90deg, rgba(229, 64, 4, 0.2) 0%, rgba(246, 145, 29, 0.2) 100%)',
                                  color: '#F6911D',
                                  fontWeight: 600,
                                  border: '1px solid rgba(246, 145, 29, 0.3)',
                                }}
                              />
                            ))
                          ) : (
                            <span className="text-gray-500 text-sm">No subjects</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="!text-gray-300">
                        {new Date(teacher.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </TableCell>
                      <TableCell align="center">
                        <div className="flex gap-2 justify-center">
                          <Button
                            variant="contained"
                            size="small"
                            href={`/teacher/${teacher.user.slug}`}
                            target="_blank"
                            startIcon={<RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              background:
                                'linear-gradient(90deg, rgba(229, 64, 4, 0.15) 0%, rgba(246, 145, 29, 0.15) 100%)',
                              color: '#F6911D',
                              border: '1px solid rgba(246, 145, 29, 0.3)',
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
                                color: 'white',
                              },
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={isActing}
                            onClick={() => handleApprove(teacher.id)}
                            startIcon={
                              isActing ? (
                                <CircularProgress size={14} sx={{ color: '#22c55e' }} />
                              ) : (
                                <HowToRegOutlinedIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            sx={{
                              background: 'rgba(34, 197, 94, 0.15)',
                              color: '#22c55e',
                              border: '1px solid rgba(34, 197, 94, 0.3)',
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                background: '#22c55e',
                                color: 'white',
                              },
                              '&:disabled': { opacity: 0.5 },
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={isActing}
                            onClick={() => handleRejectClick(teacher.id)}
                            startIcon={
                              isActing ? (
                                <CircularProgress size={14} sx={{ color: '#ef4444' }} />
                              ) : (
                                <BlockOutlinedIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            sx={{
                              background: 'rgba(239, 68, 68, 0.15)',
                              color: '#ef4444',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                background: '#ef4444',
                                color: 'white',
                              },
                              '&:disabled': { opacity: 0.5 },
                            }}
                          >
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="!text-gray-400 !py-8">
                    {hasActiveFilters
                      ? 'No results found. Try adjusting your filters.'
                      : 'No pending approvals at this time.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination — server-side */}
          <TablePagination
            component="div"
            count={meta?.total ?? 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            className="!bg-gray-800/30 !text-white backdrop-blur-sm border-t border-white/10
                           [&_.MuiTablePagination-toolbar]:!text-white
                           [&_.MuiTablePagination-selectLabel]:!text-gray-300
                           [&_.MuiTablePagination-displayedRows]:!text-gray-300
                           [&_.MuiTablePagination-select]:!text-white
                           [&_.MuiTablePagination-selectIcon]:!text-white
                           [&_.MuiIconButton-root]:!text-white
                           [&_.MuiIconButton-root:hover]:!bg-white/10"
            sx={{
              '& .MuiTablePagination-select': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                padding: '4px 8px',
              },
            }}
          />
        </TableContainer>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            background:
              snackbar.severity === 'success'
                ? 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)'
                : '#ef4444',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Reject Teacher"
        message="Are you sure you want to reject this teacher? This will permanently delete their profile and cannot be undone."
        confirmText="Reject"
        onClose={() => setConfirmDialog({ open: false, teacherId: null })}
        onConfirm={handleConfirmReject}
      />
    </div>
  );
}
